import styled, { css } from 'styled-components';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { layout, position, PositionProps, space } from 'styled-system';
import { Theme } from '../../../theme/theme';
import { LayoutProps, SpaceProps } from '../../../theme/styledProps';

// DOCS -> https://www.radix-ui.com/docs/primitives/components/dropdown-menu

const Menu = DropdownMenu.Root;

Menu.defaultProps = {
  modal: false,
};

const MenuTrigger = DropdownMenu.Trigger;

MenuTrigger.defaultProps = {
  asChild: true,
};

const MenuSeparator = styled(DropdownMenu.Separator)<{ theme: Theme }>(({ theme }) => ({
  height: 1,
  backgroundColor: theme.colors.neutral['200'],
  margin: '4px 0',
}));

const itemStyles = ({ theme }) => css`
  font-family: ${theme.fontFamily};
  font-weight: ${theme.fontWeights['500']};
  font-size: ${theme.fontSizes.body}px;
  background: ${theme.colors.bg};
  color: ${theme.colors.neutral['700']};
  user-select: none;
  padding: 6px 12px 6px 16px;
  cursor: pointer;

  &:hover,
  &:focus {
    background: ${theme.colors.neutral['100']};
    outline: none;
  }

  &[data-disabled] {
    color: ${theme.colors.neutral['400']};
    pointer-events: none;
  }
`;

const MenuItem = styled(DropdownMenu.Item)`
  ${itemStyles};
`;

const MenuTriggerItem = styled(DropdownMenu.TriggerItem)`
  ${itemStyles};
  display: flex;
  justify-content: space-between;

  &::after {
    content: '\f105';
    font-family: 'Font Awesome 5 Pro', sans-serif;
    font-weight: 500;
    font-size: 16px;
    color: ${({ theme }) => theme.colors.neutral['500']};
    align-self: center;
  }
`;

const MenuContent = styled(DropdownMenu.Content)<
  { theme: Theme } & LayoutProps & PositionProps & SpaceProps
>(
  ({ theme, width = 240 }) => ({
    boxShadow: theme.shadows['500'],
    border: `1px solid ${theme.colors.neutral['200']}`,
    borderRadius: theme.radii.medium,
    padding: '8px 0',
    minWidth: width,
    background: theme.colors.bg,
    overflowY: 'auto',
    width,
    maxHeight: 'min(540px, 50vh)',
    boxSizing: 'border-box',
  }),

  layout,
  position,
  space,
);

MenuContent.defaultProps = {
  sideOffset: 4,
};

const MenuHeader = styled(DropdownMenu.Label)<{ theme: Theme }>(({ theme }) => ({
  fontWeight: theme.fontWeights['600'],
  fontFamily: theme.fontFamily,
  lineHeight: theme.lineHeights.small,
  textTransform: 'uppercase',
  color: theme.colors.neutral['500'],
  fontSize: theme.fontSizes.small,
  letterSpacing: theme.letterSpacings.wide,
  padding: '4px 16px',
  margin: '4px 0',
}));

export { Menu, MenuTrigger, MenuSeparator, MenuItem, MenuTriggerItem, MenuContent, MenuHeader };
